// HourlyPanel — Phase 5a hourly endpoint.

import { useState } from 'react';
import { Card } from '../../primitives';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  MiniBars,
  SectionHead,
} from '../bits';
import { useHourly, type AnalyticsScope } from '../queries';
import { fmtNum } from '../../../lib/format';

interface Props {
  scope: AnalyticsScope;
}

export function HourlyPanel({ scope }: Props) {
  const q = useHourly(scope);
  const [hover, setHover] = useState<number | null>(null);

  return (
    <Card style={{ padding: 20 }}>
      <SectionHead title="시간대별 주문" sub="24h" />
      {q.isLoading && <LoadingState />}
      {q.isError && <ErrorState message={(q.error as Error).message} />}
      {q.data && q.data.length === 0 && <EmptyState />}
      {q.data && q.data.length > 0 && (
        <HourlyBody data={q.data} hover={hover} setHover={setHover} />
      )}
    </Card>
  );
}

function HourlyBody({
  data,
  hover,
  setHover,
}: {
  data: { hour: number; orders: number }[];
  hover: number | null;
  setHover: (i: number | null) => void;
}) {
  // Pad to 24 hours just in case the API skips empties.
  const padded: number[] = Array.from({ length: 24 }, (_, h) => {
    const found = data.find((p) => p.hour === h);
    return found?.orders ?? 0;
  });
  const max = Math.max(...padded);
  const peakIdx = padded.indexOf(max);
  const total = padded.reduce((a, b) => a + b, 0) || 1;
  const idx = hover != null ? hover : peakIdx;
  const h = idx.toString().padStart(2, '0');
  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          marginBottom: 8,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 13, color: 'var(--fg-3)' }}>
          {h}:00 – {((idx + 1) % 24).toString().padStart(2, '0')}:00
        </span>
        <span
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: 'var(--fg-1)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {fmtNum(padded[idx]!)}건
        </span>
        <span
          style={{
            fontSize: 12,
            color: 'var(--fg-3)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          ({((padded[idx]! / total) * 100).toFixed(1)}%)
        </span>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 12,
            color: 'var(--fg-3)',
          }}
        >
          피크{' '}
          <span style={{ color: 'var(--fg-1)', fontWeight: 600 }}>
            {peakIdx.toString().padStart(2, '0')}:00
          </span>
        </span>
      </div>
      <MiniBars
        data={padded}
        height={60}
        activeIndex={hover}
        onHover={setHover}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 6,
          fontSize: 10,
          color: 'var(--fg-4)',
        }}
      >
        <span>00</span>
        <span>06</span>
        <span>12</span>
        <span>18</span>
        <span>23</span>
      </div>
    </>
  );
}
